import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  Grid,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { ReusableDataGrid } from '@/components/common/ReusableDataGrid';
import { BookingLog } from '@/constants/types';
import { bookingLogsService } from '@/services';
import type { MRT_ColumnDef } from 'material-react-table';
import toast from 'react-hot-toast';

export const BookingLogsPage: React.FC = () => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [bookingLogs, setBookingLogs] = useState<BookingLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<BookingLog | null>(null);

  // Fetch booking logs
  const fetchBookingLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await bookingLogsService.list({
        sortDir: 'desc',
        limit: paginationModel.pageSize,
        // offset: paginationModel.page * paginationModel.pageSize,
        offset: paginationModel.page,
        search_string: searchTerm,
        sortBy: 'timestamp',
      });

      if (response.detail && response.detail[0]) {
        setBookingLogs(response.detail[0].paginatedResults);
        setTotalCount(response.detail[0].totalCount[0]?.count || 0);
      }
    } catch (error) {
      console.error('Error fetching booking logs:', error);
      toast.error('Failed to fetch booking logs');
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, searchTerm]);

  useEffect(() => {
    fetchBookingLogs();
  }, [fetchBookingLogs]);

  const handleClearSearch = () => {
    setSearchTerm('');
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleOpenDetails = (log: BookingLog) => {
    setSelectedLog(log);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedLog(null);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'success';
      case 'updated':
        return 'info';
      case 'deleted':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns: MRT_ColumnDef<BookingLog>[] = [
    {
      accessorKey: 'bookingDetails',
      header: 'Booking ID',
      size: 150,
      Cell: ({ row }) => {
        const booking = row.original.bookingDetails;
        return booking?.bookingId || '-';
      },
    },
    {
      accessorKey: 'action',
      header: 'Action',
      size: 120,
      Cell: ({ cell }) => {
        const action = cell.getValue<string>();
        return (
          <Chip
            label={action.toUpperCase()}
            size="small"
            color={getActionColor(action)}
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      accessorKey: 'userDetails',
      header: 'User',
      size: 200,
      Cell: ({ row }) => {
        const user = row.original.userDetails?.[0];
        return user ? (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {user.Name || user.UserName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.Email}
            </Typography>
          </Box>
        ) : (
          '-'
        );
      },
    },
    // {
    //   accessorKey: 'changes',
    //   header: 'Changes',
    //   size: 250,
    //   Cell: ({ cell }) => renderChanges(cell.getValue()),
    // },
    {
      accessorKey: 'timestamp',
      header: 'Timestamp',
      size: 180,
      Cell: ({ cell }) => {
        const timestamp = cell.getValue<string>();
        return (
          <Box>
            <Typography variant="body2">
              {moment(timestamp).format('MMM DD, YYYY')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {moment(timestamp).format('hh:mm A')}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleOpenDetails(row.original)}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5" component="h5" gutterBottom>
            Booking Logs
          </Typography>
          <Divider />
        </Box>
      </Box>

      {/* Search Filter */}
      <Grid container spacing={2} alignItems="center" mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Search Logs"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPaginationModel({ ...paginationModel, page: 0 });
            }}
            placeholder="Search by Booking ID"
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
          <Button
            variant="text"
            onClick={handleClearSearch}
            startIcon={<ClearIcon />}
            title="Clear Search"
            sx={{
              '& .MuiButton-startIcon': {
                mr: 0,
                ml: 0,
                display: 'flex',
                justifyContent: 'end',
              },
            }}
            disabled={searchTerm === ''}
          />
        </Grid>
      </Grid>

      {/* Data Grid */}
      <ReusableDataGrid<BookingLog>
        columns={columns}
        rows={bookingLogs}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationData={{
          totalRecords: totalCount,
        }}
        enableServerSidePagination={true}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableSorting={true}
        getRowId={(row) => row._id}
        showSerialNumber={true}
      />

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Booking Log Details</Typography>
            <Button onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Box
              sx={{
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                },
                '& td': {
                  padding: '12px 8px',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& td:first-of-type': {
                  color: 'text.secondary',
                  fontWeight: 500,
                  width: '40%',
                },
                '& td:last-of-type': {
                  fontWeight: 600,
                },
              }}
            >
              <table>
                <tbody>
                  <tr>
                    <td>Booking ID</td>
                    <td>{selectedLog.bookingDetails?.bookingId || '-'}</td>
                  </tr>
                  <tr>
                    <td>Action</td>
                    <td>
                      <Chip
                        label={selectedLog.action.toUpperCase()}
                        size="small"
                        color={getActionColor(selectedLog.action)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>User</td>
                    <td>
                      {selectedLog.userDetails?.[0]?.Name || selectedLog.userDetails?.[0]?.UserName || '-'}
                    </td>
                  </tr>
                  <tr>
                    <td>Date & Time</td>
                    <td>{moment(selectedLog.timestamp).format('MMM DD, YYYY hh:mm A')}</td>
                  </tr>
                  {selectedLog.previousData && (
                    <>
                      <tr>
                        <td colSpan={2}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
                            Previous Booking Details
                          </Typography>
                        </td>
                      </tr>
                      {selectedLog.previousData.fraternityDetails && (
                        <tr>
                          <td>Fraternity</td>
                          <td>
                            {selectedLog.previousData.fraternityDetails.name} (#{selectedLog.previousData.fraternityDetails.number})
                          </td>
                        </tr>
                      )}
                      {selectedLog.previousData.date && (
                        <tr>
                          <td>Date</td>
                          <td>{moment(selectedLog.previousData.date).format('MMM DD, YYYY')}</td>
                        </tr>
                      )}
                      {selectedLog.previousData.facilities && selectedLog.previousData.facilities.length > 0 && (
                        <tr>
                          <td>Facilities</td>
                          <td>
                            <Box>
                              {selectedLog.previousData.facilities.map((facility, idx) => {
                                const facilityDetail = selectedLog.previousData!.facilityDetails?.find(
                                  (fd) => fd._id === facility?.facilityId
                                );
                                return (
                                  <Box key={idx} sx={{ mb: idx < selectedLog.previousData!.facilities!.length - 1 ? 1 : 0 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      {facilityDetail?.name || 'TBB'}
                                    </Typography>
                                    <Typography variant="body2">
                                      Time: {facility?.time || 'N/A'}
                                    </Typography>
                                    {idx < selectedLog.previousData!.facilities!.length - 1 && <Divider sx={{ my: 1 }} />}
                                  </Box>
                                );
                              })}
                            </Box>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                  {selectedLog.newData && (
                    <>
                      <tr>
                        <td colSpan={2}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                            Current Booking Details
                          </Typography>
                        </td>
                      </tr>
                      {selectedLog.newData.fraternityDetails && (
                        <tr>
                          <td>Fraternity</td>
                          <td>
                            {selectedLog.newData.fraternityDetails.name} (#{selectedLog.newData.fraternityDetails.number})
                          </td>
                        </tr>
                      )}
                      {selectedLog.newData.date && (
                        <tr>
                          <td>Date</td>
                          <td>{moment(selectedLog.newData.date).format('MMM DD, YYYY')}</td>
                        </tr>
                      )}
                      {selectedLog.newData.facilities && selectedLog.newData.facilities.length > 0 && (
                        <tr>
                          <td>Facilities</td>
                          <td>
                            <Box>
                              {selectedLog.newData.facilities.map((facility, idx) => {
                                const facilityDetail = selectedLog.newData!.facilityDetails?.find(
                                  (fd) => fd._id === facility?.facilityId
                                );
                                return (
                                  <Box key={idx} sx={{ mb: idx < selectedLog.newData!.facilities!.length - 1 ? 1 : 0 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      {facilityDetail?.name || 'TBB'}
                                    </Typography>
                                    <Typography variant="body2">
                                      Time: {facility?.time || 'N/A'}
                                    </Typography>
                                    {idx < selectedLog.newData!.facilities!.length - 1 && <Divider sx={{ my: 1 }} />}
                                  </Box>
                                );
                              })}
                            </Box>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
